import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "./api/axios";
import { useAuth } from "./contexts/AuthContext";
import { useActivityLogger } from "./hooks/useActivity";
import { getRegistrationState, clearRegistrationState, saveRegistrationState , ensureRegistrationStateLoaded} from "./regisController";


const preferences = [
  { id: "Informasi Medis", label: "Informasi Medis", image: "/medis.png" },
  { id: "Kesehatan Mental", label: "Kesehatan Mental", image: "/mental.png" },
  { id: "Keagamaan Islam", label: "Keagamaan Islam", image: "/islam.png" },
  { id: "Keagamaan Kristen", label: "Keagamaan Kristen", image: "/kristen.png" },
  { id: "Keagamaan Katolik", label: "Keagamaan Katolik", image: "/katolik.png" },
  { id: "Keagamaan Hindu", label: "Keagamaan Hindu", image: "/hindu.png" },
  { id: "Keagamaan Buddha", label: "Keagamaan Buddha", image: "/budha.png" },
  { id: "Keagamaan Konghucu", label: "Keagamaan Konghucu", image: "/Konghucu.png" },
  { id: "Kepercayaan Lainnya", label: "Kepercayaan Lainnya", image: "/kepercayaan.png" },
];

export default function ChoosePreference() {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const userData = location.state;

  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { logUserActivity } = useActivityLogger();

  // Initialize with all preferences selected, then load saved data
  useEffect(() => {
    // Load from sessionStorage
    const formDataStr = sessionStorage.getItem("register_form_data");
    const detailsDataStr = sessionStorage.getItem("register_details_data");
    const prefsDataStr = sessionStorage.getItem("register_preferences_data");
    
    let hasData = false;
    
    // Check if we have preferences data already
    if (prefsDataStr) {
      try {
        const savedPrefs = JSON.parse(prefsDataStr);
        if (Array.isArray(savedPrefs)) {
          setSelected(savedPrefs);
          hasData = true;
        }
      } catch (e) {
        console.error("Error parsing preferences:", e);
      }
    }
    
    // If no preferences yet, set default
    if (!hasData) {
      setSelected(["Informasi Medis", "Kesehatan Mental"]);
    }
    
    // Validate we have required data
    if (!formDataStr || !detailsDataStr) {
      navigate("/register");
    }
  }, [navigate]);

  // Save data whenever selected changes (but not on initial load)
  useEffect(() => {
    if (selected.length > 0) {
      sessionStorage.setItem("register_preferences_data", JSON.stringify(selected));
    }
  }, [selected]);

  const toggle = (id) => {
    // Reset error when user interacts
    if (errorMessage) setErrorMessage("");
    
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    setErrorMessage("");
    setLoading(true);

    try {
      // Get form and details data from sessionStorage
      const formDataStr = sessionStorage.getItem("register_form_data");
      const detailsDataStr = sessionStorage.getItem("register_details_data");
      
      if (!formDataStr || !detailsDataStr) {
        throw new Error("Missing registration data");
      }
      
      const formData = JSON.parse(formDataStr);
      const detailsData = JSON.parse(detailsDataStr);
      
      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        nik: detailsData.nik,
        nama_ktp: detailsData.fullName,
        nama_samaran: detailsData.alias,
        nomor_hp: detailsData.phone,
        role: "user",
        preferences: selected.filter(p => typeof p === "string"),
      };
      
      console.log("Sending registration payload:", payload); // Debug
  
      const res = await api.post("/register", payload);
      localStorage.setItem("access_token", res.data.access_token);
      localStorage.setItem("refresh_token", res.data.refresh_token);
  
      await fetchUser();
  
      // Clear all session data on success
      sessionStorage.removeItem("register_form_data");
      sessionStorage.removeItem("register_details_data");
      sessionStorage.removeItem("register_preferences_data");
      
      logUserActivity(
        "user",
        "choose_preferences",
        "ChoosePreference",
        `User selected preferences: ${payload.preferences.join(", ")}`
      );
  
      navigate("/homelogin");
    } catch (error) {
      console.error("Registration failed:", error);
      
      let errorMsg = "Gagal membuat akun. Silakan coba lagi.";
      
      if (error.response?.data?.error) {
        const apiError = error.response.data.error;
        if (apiError === "username already exists") {
          errorMsg = "Username sudah terdaftar. Silakan gunakan username lain.";
        } else if (apiError === "email already exists") {
          errorMsg = "Email sudah terdaftar. Silakan gunakan email lain.";
        } else {
          errorMsg = apiError;
        }
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      setErrorMessage(errorMsg);
      
      logUserActivity(
        "error",
        "registration_error",
        "ChoosePreference",
        `Error during registration: ${error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBackNavigation = () => {
    // Keep preferences data when going back
    if (selected.length > 0) {
      sessionStorage.setItem("register_preferences_data", JSON.stringify(selected));
    }
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header with back button */}
      <div className="flex items-center justify-between px-4 py-4 pt-8">
        <button
          onClick={handleBackNavigation}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <div className="flex-1" /> {/* Spacer */}
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-32">
        <div className="px-6 py-4">
          <h1 className="mt-8 text-2xl font-bold text-blue-500 mb-2">
            Informasi apa yang paling kamu butuhkan?
          </h1>
          <p className="text-gray-500 mb-6">
            Kami akan beri tahu ketika video atau artikel terbaru rilis
          </p>

          {errorMessage && (
            <div className="mb-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-400"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {errorMessage}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {preferences.map((pref) => (
              <div
                key={pref.id}
                onClick={() => toggle(pref.id)}
                className={`flex items-center border rounded-xl overflow-hidden shadow-sm cursor-pointer transition-all ${
                  selected.includes(pref.id)
                    ? "ring-2 ring-blue-400 border-blue-400"
                    : "hover:ring-1 hover:ring-gray-300 border-gray-200"
                }`}
              >
                <img
                  src={pref.image}
                  alt={pref.label}
                  className="w-24 h-24 object-cover"
                />
                <div className="flex justify-between items-center w-full px-4 py-4">
                  <span className="font-medium text-gray-800">{pref.label}</span>
                  <input
                    type="checkbox"
                    checked={selected.includes(pref.id)}
                    readOnly
                    className="form-checkbox w-5 h-5 text-blue-500"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Add extra space at bottom for better scrolling */}
          <div className="h-16"></div>
        </div>
      </div>

      {/* Fixed bottom buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-white px-8 pb-6 pt-4 shadow-lg border-t border-gray-100">
        <div className="w-full max-w-md mx-auto">
          <button
            onClick={handleSubmit}
            type="button"
            disabled={loading}
            className={`w-full py-3 rounded-lg text-white font-medium transition-all ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 active:bg-blue-700"
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Mengirim...
              </div>
            ) : (
              "Pilih"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}